import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Config } from "../config";
import { useLoading } from "../hooks/use-loading";
import { User } from "../api/models/User";
import { useLogin } from "../api/useLoginApi";
import { useFetchTokenByAuthCode } from "../hooks/auth/use-fetch-token-by-auth-code";
import { useRefreshAuthToken, RefreshToken } from "../hooks/auth/use-refresh-auth-token";

interface IAuthContextProps {
  token: string | null;
  user: User | null;
  loadings: { fetchUser: boolean };
  logoutAction: () => void;
  refreshToken: () => Promise<RefreshToken | null>;
}

// @ts-ignore
const AuthContext = createContext<IAuthContextProps>();

export const AuthProvider: React.FC<any> = ({ children }) => {
  const api = useLogin();
  const { loadings, startLoading, stopLoading } = useLoading({ fetchUser: false });
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(window.localStorage.getItem("CURRENT_USER_TOKEN"));
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(() => {
    const expiresAt = window.localStorage.getItem("CURRENT_USER_TOKEN_EXPIRES_AT");
    return expiresAt ? new Date(expiresAt) : null;
  });

  const { refreshToken: refreshTokenFn } = useRefreshAuthToken();
  const { fetchTokenAfterLogin } = useFetchTokenByAuthCode();

  const updateToken = useCallback((newToken: RefreshToken | null, redirectUrl?: string | null) => {
    if (newToken) {
      const expirationDate = new Date(Date.now() + newToken.expires_in * 1000);
      window.localStorage.setItem("CURRENT_USER_TOKEN", newToken.access_token);
      window.localStorage.setItem("CURRENT_USER_TOKEN_EXPIRES_AT", expirationDate.toISOString());
      setToken(newToken.access_token);
      setTokenExpiresAt(expirationDate);
    } else {
      window.localStorage.removeItem("CURRENT_USER_TOKEN");
      window.localStorage.removeItem("CURRENT_USER_TOKEN_EXPIRES_AT");
      const redirect_uri = encodeURIComponent(`${window.location.protocol}//${window.location.host}/auth-callback`);
      const state = encodeURIComponent(window.location.href);
      window.location.href = redirectUrl ?? `${Config.KEYCLOAK_URL}&redirect_uri=${redirect_uri}&state=${state}`;
    }
  }, []);

  const authorize = useCallback(async () => {
    if (token) {
      return;
    }

    try {
      // @ts-ignore
      const [newToken, redirectUrl]: [RefreshToken, string | null] = await fetchTokenAfterLogin();
      updateToken(newToken, redirectUrl);
    } catch (error) {
      updateToken(null);
    }
  }, [token, fetchTokenAfterLogin, refreshTokenFn, updateToken]);

  const fetchUser = useCallback(async () => {
    try {
      startLoading("fetchUser");
      const currentUser = await api.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
      updateToken(null);
    } finally {
      stopLoading("fetchUser");
    }
  }, [api, startLoading, stopLoading, updateToken]);

  const isTokenExpiringSoon = useCallback(() => {
    if (!tokenExpiresAt) return false;
    const now = new Date();
    return (now.getTime() + (5 * 60 * 1000)) >= tokenExpiresAt.getTime();
  }, [tokenExpiresAt]);

  useEffect(() => {
    const initAuth = async () => {
      if (!token) {
        await authorize();
      }
      await fetchUser();
    };

    void initAuth();
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (token && tokenExpiresAt && isTokenExpiringSoon()) {
        const newToken = await refreshTokenFn();
        updateToken(newToken);
      }
    }, 30_000);
    return () => clearInterval(timer);
  }, [token, tokenExpiresAt, isTokenExpiringSoon, refreshTokenFn, updateToken]);

  const logoutAction = useCallback(() => {
    setUser(null);
    const redirect_uri = encodeURIComponent(`${window.location.protocol}//${window.location.host}/auth-callback`);
    updateToken(null, `${Config.KEYCLOAK_LOGOUT_URL}redirect_uri=${redirect_uri}`);
  }, [updateToken]);

  return (
    <AuthContext.Provider value={{ token, user, logoutAction, loadings, refreshToken: refreshTokenFn }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
